import React from 'react';
import { InfoTip, MetricKey } from '@/components/shared';

interface StatTileProps {
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  /** Definition shown behind the ⓘ. */
  info?: MetricKey;
  /** A period-over-period chip, drawn beside the value. */
  delta?: React.ReactNode;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}

const TONES: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'text-white',
  good: 'text-emerald-300',
  warn: 'text-amber-300',
  bad: 'text-red-300',
};

/** Compact numeric stat card for the economy dashboard. */
export function StatTile({ label, value, sublabel, tone = 'default', info, delta }: StatTileProps) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
        {label}
        {info && <InfoTip metric={info} />}
      </p>
      <p className={`mt-2 text-2xl font-bold tabular-nums flex items-center gap-2 flex-wrap ${TONES[tone]}`}>
        {value}
        {delta}
      </p>
      {sublabel && <p className="mt-1 text-xs text-gray-500">{sublabel}</p>}
    </div>
  );
}

/** Section wrapper: titled card that panels drop charts/tables into. */
export function Panel({
  title,
  subtitle,
  children,
  actions,
  info,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  info?: MetricKey;
}) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-white">
            {title}
            {info && <InfoTip metric={info} />}
          </h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function formatCoins(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US');
}

export function formatPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(1)}%`;
}

export function formatRatio(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toFixed(2);
}
